import { useEffect } from 'react';
import { useApplicationDispatch, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { selectValueMatrixTableInputsRequest, setValueMatrixTableInputsRequest } from '../../../../../Redux/Slices/ValueMatrixCalculatorSlice';
import CheckboxDropdown from '../../../../Components/Form/Inputs/CheckboxDropdown';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from '../ValueMatrixCalculator.module.scss';
import { ContractGroupName } from '../ValueMatrixDefinitions';

interface IValueMatrixTermClassificationDropdownProps {
    termClassificationOptions: {
        label: string;
        value: {
            contractMonths: number[];
            contractGroupName: ContractGroupName;
        };
    }[];
}

const minimumNumberOfSelections = 1;
const maximumSelections = 5;

const ValueMatrixTermClassificationDropdown: React.FC<IValueMatrixTermClassificationDropdownProps> = (props: IValueMatrixTermClassificationDropdownProps) => {
    const [translations] = useLanguage();
    const dispatch = useApplicationDispatch();
    const valueMatrixTableInputs = useApplicationSelector(selectValueMatrixTableInputsRequest);
    const valueMatrixStripTypeIsNotTwelveMonthStrip = valueMatrixTableInputs.stripType !== 'twelveMonthStrip';
    const valueMatrixStripTypeIsNotSpot = valueMatrixTableInputs.stripType !== 'spot';

    useEffect(() => {
        const termClassificationAlreadySelected =
            (valueMatrixTableInputs.termClassification ?? []).length > 0 &&
            (valueMatrixTableInputs.termClassification ?? []).every((x) =>
                props.termClassificationOptions.find((y) => y.value.contractGroupName === x.contractGroupName),
            );

        if ((valueMatrixStripTypeIsNotTwelveMonthStrip && valueMatrixStripTypeIsNotSpot) || termClassificationAlreadySelected) {
            return;
        }

        dispatch(
            setValueMatrixTableInputsRequest({
                ...valueMatrixTableInputs,
                termClassification: [props.termClassificationOptions[0].value],
                isCascadeUpdating: false,
            }),
        );
    }, [props.termClassificationOptions]);

    return (
        <div className={styles.value_matrix_dropdown}>
            <CheckboxDropdown
                testId="ValueMatrixTermClassificationDropdown"
                placeholder={props.termClassificationOptions
                    .filter((x) => (valueMatrixTableInputs.termClassification ?? []).map((y) => y.contractGroupName).includes(x.value.contractGroupName))
                    .map((x) => x.label)
                    .join(', ')}
                options={props.termClassificationOptions}
                values={valueMatrixTableInputs.termClassification ?? []}
                handleOptionSelect={(value) => {
                    dispatch(
                        setValueMatrixTableInputsRequest({
                            ...valueMatrixTableInputs,
                            termClassification: value,
                            updateTriggeredBy: 'generalFieldSelectionOrUpdate',
                        }),
                    );
                }}
                disabled={valueMatrixTableInputs.stripType === 'spot'}
                minimumSelectionLimit={minimumNumberOfSelections}
                maximumSelectionLimit={maximumSelections}
                limitMessage={translations.calculators.valueMatrix.text.limitMessage}
                track={{
                    eventCategory: 'ValueMatrix',
                    eventAction: 'TermClassificationChanged',
                    eventValue: (valueMatrixTableInputs.termClassification ?? []).map((x) => x.contractGroupName).join(','),
                }}
            />
        </div>
    );
};

export default ValueMatrixTermClassificationDropdown;
